// METIS · CURRENT STEP — core of EXECUTE. Reads the orb's own Step + the existing
// runner timer (never a second timer) and today's agenda items from c.data.
import { mmss } from '../../runner';
import { dateKeyOf } from '../../time';
import type { OrbWidgetAction, OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel, openAction } from './util';

const BASE = { id: 'metis', toolId: 'metis', orderFunction: 'execute', title: 'CURRENT STEP', deityName: 'METIS', agentId: 'metis' } as const;

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

export function useMetisWidget(c: WidgetCtx): OrbWidgetModel {
  const step = c.step;
  if (!step) return emptyModel(BASE, c, 'No active Step', { label: 'Plan a Step', run: () => c.openTool('kyrs'), tone: 'gold' });

  const todayKey = dateKeyOf(c.now);
  const day = c.data.items.filter((i) => i.date === todayKey);
  const pos = day.findIndex((i) => i.key === step.key);
  const seq = pos >= 0 ? `Sequence ${pos + 1}/${day.length}` : null;
  const nextItem = pos >= 0 ? day.slice(pos + 1).find((i) => !i.completed) : undefined;
  const nextText = nextItem?.title ?? c.today.nextItem?.text ?? null;

  const r = c.runner;
  const left = r.finished ? 0 : r.left;
  const pastSlot = !step.completed && !!step.start && step.start.getTime() + step.minutes * 60000 < c.now.getTime();
  const status: OrbWidgetModel['status'] = step.completed ? 'quiet' : pastSlot ? 'attention' : 'active';

  const timeLine = step.completed ? 'Done' : `${mmss(left)} remaining`;
  const lines = [seq ? `${timeLine} · ${seq}` : timeLine];
  if (r.n > 1) lines.push(`Routine step ${Math.min(r.idx + 1, r.n)}/${r.n}${r.cur ? ` · ${clip(r.cur.title, 28)}` : ''}`);
  if (nextText) lines.push(`Next: ${clip(nextText, 44)}`);

  const summary = step.completed ? `${clip(step.title, 28)} · done` : `${clip(step.title, 26)} · ${Math.ceil(left / 60)}m`;

  const primary: OrbWidgetAction = step.completed || r.finished
    ? { label: r.finished ? 'Finished' : 'Completed', run: () => {}, tone: 'ok', disabled: true }
    : r.running
      ? { label: 'Pause', run: r.pause, tone: 'gold' }
      : { label: 'Play', run: r.play, tone: 'gold' };

  const secondary: OrbWidgetAction[] = [
    { label: 'Done', run: () => void c.acts.done(step), tone: 'ok', disabled: step.completed || !(step.rowId || step.taskId) || c.acts.busy },
  ];
  if (pastSlot) secondary.push({ label: 'Recover sequence', run: () => c.openTool('metis'), tone: 'warn' });
  secondary.push(openAction(c, 'metis', 'Metis'));

  return {
    ...BASE,
    status,
    summary: clip(summary, 40),
    alert: pastSlot ? 'Past its slot — recover the sequence' : null,
    lines,
    primaryAction: primary,
    secondaryActions: secondary,
    signals: { active: true, related: true, alerted: pastSlot },
  };
}
